/**
 * Per-mutation retry-safety policy — v.1-D §7.9.
 *
 * Mutations listed here are idempotent (natural-key dedupe or patch / no-op
 * semantics) and the error-toast Retry button is therefore safe to surface
 * without warning the user. All other mutations must NOT auto-retry; the
 * `useMutationWithToast` hook consults `isRetrySafe(mutationName)` to gate
 * the Retry action.
 *
 * Whitelist source: v.1-D §7.9 (cross-team quarterly review per CLAUDE.md §6.8).
 *
 * Ported verbatim by T304 from `frontend-v2/src/lib/mutation-policy.ts`.
 */

export const RETRY_SAFE_MUTATIONS: ReadonlySet<string> = new Set([
  'alerts.acknowledgeAlert',
  'alerts.acknowledgeAllUnacknowledged',
  'alerts.resolveAlert',
  'alerts.resolveChannelAlerts',
  'automations.setEnabled',
  'templates.incrementUsage',
  // T317 / V3I-FR-012 Category-C drift fix C6:
  //   `channels.updateChannel` is REMOVED — it does not exist in
  //   `convex/mutations/channels.ts` (PRD §7.2 C6, drift confirmed by
  //   T-0.0.5 and the §6 baseline check). The per-field repoint replaces
  //   it with `channels.setBridgeBufferSeconds` (line 70 of that file —
  //   admin-gated, 1-120 validation, patch-style write to a single
  //   column). A retry replays the same `{ channelId, seconds }` and the
  //   row converges; the value range is the only failure path, and a
  //   retry will surface the same range error again — safe.
  'channels.setBridgeBufferSeconds',
  'organizations.updateOrg', // patch semantics
  'organizations.updateMemberRole', // patch semantics
  'alertPreferences.upsert',
  // ──────────────────────────────────────────────────────────────────────
  // T316 / V3I-FR-012 Category-A retry-policy additions
  // ──────────────────────────────────────────────────────────────────────
  // A1 — `assets.updateAsset` patches the asset metadata. Last-write-wins
  // semantics; a retry replays the same { title, description, tags } and
  // the row converges. Safe.
  'assets.updateAsset',
  // A3 — `batchOperations.createJob` is RETRY_SAFE because T309 added the
  // `idempotencyKey` arg + `jobIdempotency` response cache: a retry within
  // the TTL returns the cached jobId — no second batchJobs row lands.
  'batchOperations.createJob',
  // A8 — `automations.update` is patch-style (optional fields, last-write
  // wins); safe. `automations.remove` is NOT added — destructive deletes
  // are not retry-safe on principle.
  'automations.update',
  // ──────────────────────────────────────────────────────────────────────
  // T317 / V3I-FR-012 Category-C retry-policy notes (NEGATIVE entries)
  // ──────────────────────────────────────────────────────────────────────
  // C5 — `annotations.updateStatus` is INTENTIONALLY NOT listed here.
  //      Per PRD §6.7 line 218 ("C5 `annotations.updateStatus` — NOT
  //      retryable (state transition)") and the task brief, status
  //      transitions are single-write semantics: an Accept → Reject retry
  //      after a transient network failure has different observable
  //      semantics than the original click, so the wrapper must NOT show
  //      Retry on the error toast for these calls.
  // 2026-06-15 asset soft-delete (trash): both are patch-style and idempotent
  // — `moveToTrash` re-sets `trashedAt`, `restoreFromTrash` clears it; a retry
  // converges to the same state. Safe.
  'assetQueries.moveToTrash',
  'assetQueries.restoreFromTrash',
  // ──────────────────────────────────────────────────────────────────────
  // NMW-410 / NMW-411 tag editing — both are replace/converge idempotent.
  // ──────────────────────────────────────────────────────────────────────
  // `assets.applyAssetTags` is full replace-to-match: a retry replays the
  // same authoritative { tags } set and the assetTags index + assets.tags
  // mirror converge. Safe.
  'assets.applyAssetTags',
  // `assets.bulkApplyAssetTags` adds/removes ONE typed tag across the given
  // assets via the same replace-to-match path; a retry with the same
  // { assetIds, tag, kind, op } is a no-op once applied. Safe.
  'assets.bulkApplyAssetTags',
  // ──────────────────────────────────────────────────────────────────────
  // NMW-913 / NMW-929 folder placement — both are single-field patches.
  // ──────────────────────────────────────────────────────────────────────
  // `assets.moveAssetsToFolder` / `channels.moveChannelsToFolder` write
  // `folderId` and nothing else (no `updatedAt` bump), and the whole batch
  // is validated before the first patch. A retry with the same
  // { ids, folderId } re-files the same rows to the same place and
  // converges; a partially-applied first attempt converges too. Safe.
  'assets.moveAssetsToFolder',
  'channels.moveChannelsToFolder',
]);

export function isRetrySafe(mutationName: string): boolean {
  return RETRY_SAFE_MUTATIONS.has(mutationName);
}
